import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TaskFilterInputObject, TaskScopeNameEnum } from 'graphql-common';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import Modal from '@lib/components/Modal/Modal';
import { ModalMode } from '@lib/components/Modal/enums';
import useTasksDuplicatesQueryHook from 'utils/fetch/hooks/useTasksDuplicatesQueryHook';
import { GetUrlParams } from '@lib/enums/urls';
import { TaskDuplicationList } from './TaskDuplicationList';
import styles from './TaskDuplicationWarning.module.scss';
import { AssignationsType } from '../enums';
import getAdditionalFilters from './utils/getAdditionalFilters';
import { TaskDuplicationWarningProps } from './types';

export default function TaskDuplicationWarning(
  props: TaskDuplicationWarningProps,
) {
  const {
    assignationsType,
    assetIds,
    siteAreaIds,
    taskName,
    templateVersionId,
  } = props;
  const { t } = useTranslation();

  const additionalFilters = useMemo<TaskFilterInputObject[]>(
    () =>
      getAdditionalFilters({
        assignationsType,
        assetIds,
        siteAreaIds,
        taskName,
        templateVersionId,
      }),
    [assignationsType, assetIds, siteAreaIds, taskName, templateVersionId],
  );

  const [debouncedFilters, setDebouncedFilters] =
    useState<TaskFilterInputObject[]>(additionalFilters);

  useEffect(() => {
    if (!additionalFilters.length) {
      setDebouncedFilters([]);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setDebouncedFilters(additionalFilters);
    }, 400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [additionalFilters]);

  const shouldSkip = debouncedFilters.length === 0;

  const { collection } = useTasksDuplicatesQueryHook({
    additionalFilters: debouncedFilters,
    scopeName: TaskScopeNameEnum.OnValidationOrValidated,
    skip: shouldSkip,
    usePrevious: false,
    paginationParams: {
      [GetUrlParams.Page]: '1',
      [GetUrlParams.PerPage]: '1',
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const onModalOpen = () => setIsModalOpen(true);
  const onModalClose = () => setIsModalOpen(false);

  useEffect(() => {
    if (shouldSkip && isModalOpen) {
      setIsModalOpen(false);
    }
  }, [shouldSkip, isModalOpen]);

  let text: string | undefined;
  if (assignationsType === AssignationsType.Area) {
    text = t('tasks-duplication-warning-areas');
  } else if (assignationsType === AssignationsType.Equipment) {
    text = t('tasks-duplication-warning-assets');
  }

  if (collection.length) {
    return (
      <>
        <SupportiveMessage
          color={SupportiveMessageColor.Yellow}
          type={SupportiveMessageType.FilledWithLeftBorder}
          text={text}
          actions={
            <Button
              buttonText={t('show-details')}
              buttonType={ButtonTypes.primaryFilled}
              buttonSize={ButtonSizes.small}
              onClick={onModalOpen}
            />
          }
          hasBottomSpace={false}
          className={styles.supportiveMessage}
        />
        <Modal
          isOpen={isModalOpen}
          onClose={onModalClose}
          caption={t('tasks-may-be-duplicated')}
          mode={ModalMode.FullScreen}
          modalContentClassName={styles.modalContentClassName}
        >
          <TaskDuplicationList
            filters={debouncedFilters}
            isOpen={isModalOpen}
            skip={shouldSkip}
          />
        </Modal>
      </>
    );
  }
  return null;
}
